import { defineStore } from "pinia";

import type { PiniaStore, ReachableStore } from "../shared";
import * as attachment from "./attachment";
import * as chat from "./chat";
import * as comment from "./comment";
import * as dartboard from "./dartboard";
import * as doc from "./doc";
import * as form from "./form";
import * as helpers from "./helpers";
import * as notification from "./notification";
import * as option from "./option";
import * as paginationState from "./paginationState";
import * as property from "./property";
import * as relationship from "./relationship";
import * as skill from "./skill";
import * as space from "./space";
import * as status from "./status";
import * as task from "./task";
import * as taskKind from "./taskKind";
import * as user from "./user";
import * as view from "./view";
import * as webhook from "./webhook";

type State = attachment.State &
  chat.State &
  comment.State &
  dartboard.State &
  doc.State &
  form.State &
  helpers.State &
  notification.State &
  option.State &
  paginationState.State &
  property.State &
  relationship.State &
  skill.State &
  space.State &
  status.State &
  task.State &
  taskKind.State &
  user.State &
  view.State &
  webhook.State;

type Getters = attachment.Getters &
  chat.Getters &
  comment.Getters &
  dartboard.Getters &
  doc.Getters &
  form.Getters &
  helpers.Getters &
  notification.Getters &
  option.Getters &
  paginationState.Getters &
  property.Getters &
  relationship.Getters &
  skill.Getters &
  space.Getters &
  status.Getters &
  task.Getters &
  taskKind.Getters &
  user.Getters &
  view.Getters &
  webhook.Getters;

type Actions = attachment.Actions &
  chat.Actions &
  comment.Actions &
  dartboard.Actions &
  doc.Actions &
  form.Actions &
  helpers.Actions &
  notification.Actions &
  option.Actions &
  paginationState.Actions &
  property.Actions &
  relationship.Actions &
  skill.Actions &
  space.Actions &
  status.Actions &
  task.Actions &
  taskKind.Actions &
  user.Actions &
  view.Actions &
  webhook.Actions;

type Name = "DataStore";
export type DataStore = PiniaStore<Name, State, Getters, Actions>;

const useStore: ReachableStore<Name, State, Getters, Actions> = defineStore("DataStore", {
  state: (): State => ({
    ...attachment.state,
    ...chat.state,
    ...comment.state,
    ...dartboard.state,
    ...doc.state,
    ...form.state,
    ...helpers.state,
    ...notification.state,
    ...option.state,
    ...paginationState.state,
    ...property.state,
    ...relationship.state,
    ...skill.state,
    ...space.state,
    ...status.state,
    ...task.state,
    ...taskKind.state,
    ...user.state,
    ...view.state,
    ...webhook.state,
  }),
  undo: true,
  getters: {
    ...attachment.getters,
    ...chat.getters,
    ...comment.getters,
    ...dartboard.getters,
    ...doc.getters,
    ...form.getters,
    ...helpers.getters,
    ...notification.getters,
    ...option.getters,
    ...paginationState.getters,
    ...property.getters,
    ...relationship.getters,
    ...skill.getters,
    ...space.getters,
    ...status.getters,
    ...task.getters,
    ...taskKind.getters,
    ...user.getters,
    ...view.getters,
    ...webhook.getters,
  },
  actions: {
    ...attachment.actions,
    ...chat.actions,
    ...comment.actions,
    ...dartboard.actions,
    ...doc.actions,
    ...form.actions,
    ...helpers.actions,
    ...notification.actions,
    ...option.actions,
    ...paginationState.actions,
    ...property.actions,
    ...relationship.actions,
    ...skill.actions,
    ...space.actions,
    ...status.actions,
    ...task.actions,
    ...taskKind.actions,
    ...user.actions,
    ...view.actions,
    ...webhook.actions,
  },
});

export default useStore;
